import React from "react";
import { Row, Col, Button, Form } from "react-bootstrap";
import Select from "react-select";
import { PermissionWrapper } from "../../PermissionWrapper/PermissionWrapper";
import type { DateRange, SportOption } from "../../../pages/AcademyEnrolments/Enrolments/useAcademyEnrolments";

const ENROLLED_THROUGH_OPTIONS = [
  { value: "", label: "All Sources" },
  { value: "Excel", label: "Excel" },
  { value: "Online", label: "Online" },
  { value: "Offline", label: "Offline" },
  { value: "Partner App - Offline", label: "Partner App - Offline" },
];

interface EnrolmentsFiltersProps {
  searchValue: string;
  onSearchChange?: (value: string) => void;
  enrolledThroughValue: string;
  onEnrolledThroughChange?: (value: string) => void;
  sportValue: string;
  onSportChange?: (value: string) => void;
  sportOptions?: SportOption[];
  dateRange: DateRange;
  onDateRangeChange?: (range: DateRange) => void;
  paidAt: string;
  onPaidAtChange?: (value: string) => void;
  onAddEnrolmentClick?: () => void;
  onBulkUploadClick?: () => void;
  onDownloadClick?: () => void;
  selectedCount?: number;
  onBulkDeleteClick?: () => void;
}

const EnrolmentsFilters: React.FC<EnrolmentsFiltersProps> = ({
  searchValue,
  onSearchChange,
  enrolledThroughValue,
  onEnrolledThroughChange,
  sportValue,
  onSportChange,
  sportOptions = [],
  dateRange,
  onDateRangeChange,
  paidAt,
  onPaidAtChange,
  onAddEnrolmentClick,
  onBulkUploadClick,
  onDownloadClick,
  selectedCount = 0,
  onBulkDeleteClick,
}) => {
  const allSportOptions = [{ value: "", label: "All Sports" }, ...sportOptions];
  return (
  <div className="enrolments-filters-container">
    <Row className="g-2 align-items-end flex-nowrap flex-wrap">
      <Col xs={12} md="auto" className="flex-grow-1" style={{ minWidth: 180 }}>
        <Form.Control
          type="search"
          placeholder="Search by name, phone or centre"
          autoComplete="off"
          name="academyEnrolmentsSearch"
          spellCheck={false}
          value={searchValue}
          onChange={(e) => onSearchChange?.(e.target.value)}
        />
      </Col>
      <Col xs={6} md="auto" style={{ width: 150 }}>
        <Form.Label className="mb-1 text-muted" style={{ fontSize: 11 }}>
          Enrolled Through
        </Form.Label>
        <Select
          classNamePrefix="react-select"
          options={ENROLLED_THROUGH_OPTIONS}
          isClearable={false}
          value={
            ENROLLED_THROUGH_OPTIONS.find((opt) => opt.value === enrolledThroughValue) ??
            ENROLLED_THROUGH_OPTIONS[0]
          }
          onChange={(option) => onEnrolledThroughChange?.(option?.value ?? "")}
        />
      </Col>
      <Col xs={6} md="auto" style={{ width: 140 }}>
        <Form.Label className="mb-1 text-muted" style={{ fontSize: 11 }}>
          Sport
        </Form.Label>
        <Select
          classNamePrefix="react-select"
          options={allSportOptions}
          isClearable={false}
          value={
            allSportOptions.find((opt) => opt.value === sportValue) ?? allSportOptions[0]
          }
          onChange={(option) => onSportChange?.(option?.value ?? "")}
        />
      </Col>
      <Col xs={6} md={2}>
        <Form.Label className="mb-1 text-muted" style={{ fontSize: 11 }}>
          Start Date
        </Form.Label>
        <div className="enrolment-date-input-wrapper">
          <Form.Control
            type="date"
            value={dateRange.startDate}
            onChange={(e) =>
              onDateRangeChange?.({ ...dateRange, startDate: e.target.value })
            }
          />
          {dateRange.startDate && (
            <button
              type="button"
              className="enrolment-date-clear-btn"
              onClick={() =>
                onDateRangeChange?.({ ...dateRange, startDate: "" })
              }
              title="Clear start date"
            >
              <i className="ri-close-line" />
            </button>
          )}
        </div>
      </Col>
      <Col xs={6} md={2}>
        <Form.Label className="mb-1 text-muted" style={{ fontSize: 11 }}>
          End Date
        </Form.Label>
        <div className="enrolment-date-input-wrapper">
          <Form.Control
            type="date"
            value={dateRange.endDate}
            min={dateRange.startDate || undefined}
            onChange={(e) =>
              onDateRangeChange?.({ ...dateRange, endDate: e.target.value })
            }
          />
          {dateRange.endDate && (
            <button
              type="button"
              className="enrolment-date-clear-btn"
              onClick={() =>
                onDateRangeChange?.({ ...dateRange, endDate: "" })
              }
              title="Clear end date"
            >
              <i className="ri-close-line" />
            </button>
          )}
        </div>
      </Col>
      <Col xs={6} md={2}>
        <Form.Label className="mb-1 text-muted" style={{ fontSize: 11 }}>
          Paid At
        </Form.Label>
        <div className="enrolment-date-input-wrapper">
          <Form.Control
            type="date"
            value={paidAt}
            onChange={(e) => onPaidAtChange?.(e.target.value)}
          />
          {paidAt && (
            <button
              type="button"
              className="enrolment-date-clear-btn"
              onClick={() => onPaidAtChange?.("")}
              title="Clear paid at date"
            >
              <i className="ri-close-line" />
            </button>
          )}
        </div>
      </Col>
    </Row>
    <Row className="g-2 mt-1 justify-content-md-end align-items-center">
      {/* <Col xs="auto">
        <Button
          variant="outline-secondary"
          className="d-flex align-items-center gap-1"
          style={{ height: 38 }}
          onClick={onDownloadClick}
        >
          <i className="ri-download-line" />
          Download
        </Button>
      </Col> */}
      <PermissionWrapper
        anyOfPermissions={[
          "academy_management.admin",
          "academy_management.enrollments.manage",
        ]}
        hideWhenDisabled
      >
        <>
          <Col xs="auto">
            <Button
              variant="outline-danger"
              className="d-flex align-items-center gap-1"
              style={{ height: 38 }}
              disabled={selectedCount === 0}
              onClick={onBulkDeleteClick}
            >
              <i className="ri-delete-bin-line" style={{ lineHeight: 1, paddingBottom: 4 }} />
              {`Delete Enrolment${selectedCount > 1 ? "s" : ""}${selectedCount > 0 ? ` (${selectedCount})` : ""}`}
            </Button>
          </Col>
          <Col xs="auto">
            <Button
              variant="outline-primary"
              className="d-flex align-items-center gap-1"
              style={{ height: 38 }}
              onClick={onBulkUploadClick}
            >
              <i className="ri-upload-line" />
              Bulk Upload
            </Button>
          </Col>
          <Col xs="auto">
            <Button
              variant="primary"
              className="d-flex align-items-center gap-1"
              style={{ height: 38 }}
              onClick={onAddEnrolmentClick}
            >
              Add Enrolment +
            </Button>
          </Col>
        </>
      </PermissionWrapper>
    </Row>
  </div>
  );
};

export default EnrolmentsFilters;
